import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { settlementDashboardNav, settlementPhases, type PhaseStatus } from './settlementNavigationConfig';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import LogoutIcon from '../../assets/icons/logout-icon.svg';

type Props = {
  children: React.ReactNode;
  /** Runtime phase statuses keyed by phase id. Unlocks / marks progress in the sidebar. */
  phaseStatuses?: Record<string, PhaseStatus>;
};

function classNames(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(' ');
}

const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * Settlement-specific sidebar navigation.
 * Modelled after the planning-side Navigation.tsx but themed for the settlement workflow
 * and with phase-progress indicators (filled / hollow circles).
 */
export default function SettlementSidebar({ children, phaseStatuses }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logoutSession } = useSessionPersistence();

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia(DESKTOP_QUERY).matches;
  });

  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia(DESKTOP_QUERY).matches;
  });

  const [headerHeight, setHeaderHeight] = useState<number>(70);

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const sync = () => {
      const desktop = mql.matches;
      setIsDesktop(desktop);
      setIsOpen(desktop);
    };
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const headerEl = document.getElementById('app-header');
    if (!headerEl) return;
    const measure = () => setHeaderHeight(headerEl.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(headerEl);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Figma sidebar: 239px wide
  const navWidth = useMemo(() => (isOpen ? 'w-[239px]' : 'w-14'), [isOpen]);
  const hamburgerTop = headerHeight + 12;

  /** Resolve the status for a phase, falling back to the static default. */
  const getPhaseStatus = (phaseId: string): PhaseStatus => {
    if (phaseStatuses && phaseStatuses[phaseId]) return phaseStatuses[phaseId];
    const phase = settlementPhases.find((p) => p.id === phaseId);
    return phase?.defaultStatus ?? 'locked';
  };

  /** Phase indicator — Figma: 18x18 circles */
  const PhaseIndicator = ({ phaseId }: { phaseId: string }) => {
    const status = getPhaseStatus(phaseId);
    if (status === 'complete') {
      return (
        <span className="inline-block w-[18px] h-[18px] rounded-full bg-green-600 shrink-0" title="Complete" />
      );
    }
    if (status === 'in-progress') {
      return (
        <span className="inline-block w-[18px] h-[18px] rounded-full border-2 border-blue-600 bg-blue-100 shrink-0" title="In Progress" />
      );
    }
    if (status === 'not-started') {
      return (
        <span className="inline-block w-[18px] h-[18px] rounded-full border-2 border-grey-300 shrink-0" title="Not Started" />
      );
    }
    // locked
    return (
      <span className="inline-block w-[18px] h-[18px] rounded-full border-2 border-grey-300 shrink-0" title="Locked" />
    );
  };

  /** Check if a nav item corresponds to a workflow phase so we can show an indicator. */
  const phaseForPath = (path: string): string | null => {
    const match = settlementPhases.find((p) => p.path === path);
    return match ? match.id : null;
  };

  return (
    <div
      className="flex w-full overflow-hidden bg-grey-200 antialiased"
      style={{ height: `calc(100dvh - ${headerHeight}px)` }}
    >
      <div className="flex flex-1 min-h-0 min-w-0 w-full">
        {/* Mobile floating trigger */}
        {!isDesktop && !isOpen && (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            aria-label="Open navigation"
            className="fixed left-[12px] z-50 w-9 h-9 flex flex-col justify-center items-center space-y-[3px] rounded-md bg-white border border-grey-300 shadow-sm hover:bg-blue-100/50"
            style={{ top: hamburgerTop }}
          >
            <div className="w-[17px] h-0.5 bg-blue-900" />
            <div className="w-[17px] h-0.5 bg-blue-900" />
            <div className="w-[17px] h-0.5 bg-blue-900" />
          </button>
        )}

        {/* Mobile backdrop */}
        {!isDesktop && isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          className={classNames(
            'border-r border-grey-300 bg-white transition-all duration-300 flex flex-col overflow-hidden',
            isDesktop && 'shrink-0',
            isDesktop && navWidth,
            !isDesktop &&
              'fixed left-0 top-0 h-[calc(100vh)] z-50 w-[239px] shadow-lg transition-transform duration-300',
            !isDesktop && (isOpen ? 'translate-x-0' : '-translate-x-full')
          )}
        >
          {/* Hamburger toggle */}
          <div
            className={classNames(
              'shrink-0 z-10 bg-white flex items-center px-3 pt-2 pb-2 border-b border-grey-300/50',
              isDesktop ? (isOpen ? 'justify-start' : 'justify-center') : 'justify-start'
            )}
          >
            <button
              type="button"
              onClick={() => setIsOpen((v) => !v)}
              className="w-9 h-9 flex flex-col justify-center items-center space-y-[3px] rounded-md hover:bg-blue-100/50 transition-colors"
              aria-label="Toggle menu"
            >
              <div className="w-[17px] h-0.5 bg-blue-900" />
              <div className="w-[17px] h-0.5 bg-blue-900" />
              <div className="w-[17px] h-0.5 bg-blue-900" />
            </button>
          </div>

          {/* Nav content */}
          {isOpen && (
            <div className="flex flex-col min-h-0 h-full">
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {settlementDashboardNav.map((group) => (
                  <div key={group.title} className="mb-5 font-sans">
                    {/* Figma: 14px uppercase section header */}
                    <div className="mb-[12px] text-[14px] font-semibold uppercase tracking-wide text-black">
                      {group.title}
                    </div>

                    {group.sections.map((section, idx) => (
                      <div key={`${group.title}-${section.title || idx}`} className="mb-7">
                        {section.title ? (
                          <div className="mb-2 text-sm font-medium text-blue-900">
                            {section.title}
                          </div>
                        ) : null}

                        <div className="space-y-3">
                          {section.items.map((item) => {
                            const isActive =
                              item.match === 'exact'
                                ? location.pathname === item.path
                                : location.pathname.startsWith(item.path);

                            const phaseId = phaseForPath(item.path);

                            return (
                              <NavLink
                                key={item.path}
                                to={item.disabled ? location.pathname : item.path}
                                aria-disabled={item.disabled ? true : undefined}
                                tabIndex={item.disabled ? -1 : 0}
                                className={classNames(
                                  'rounded-md px-4 py-[2px] text-[16px] leading-6 font-medium transition-colors flex items-center gap-[10px]',
                                  item.disabled && 'opacity-50 cursor-not-allowed',
                                  !item.disabled && 'hover:bg-blue-100/50',
                                  isActive && !item.disabled && 'bg-blue-100 text-blue-900',
                                  !isActive && 'text-black'
                                )}
                                onClick={(e) => {
                                  if (item.disabled) e.preventDefault();
                                  if (!isDesktop && !item.disabled) setIsOpen(false);
                                }}
                              >
                                {phaseId && <PhaseIndicator phaseId={phaseId} />}
                                {item.icon && (
                                  <img src={item.icon} alt={item.label} className="w-5 h-5" />
                                )}
                                <span className="truncate">{item.label}</span>
                              </NavLink>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Logout */}
              <div className="shrink-0 border-t border-grey-300 px-4 py-4 bg-white mt-auto">
                <button
                  onClick={async () => {
                    try {
                      console.log('[SettlementSidebar] Initiating secure logout...');
                      localStorage.removeItem('account-token');
                      localStorage.removeItem('account-refresh-token');
                      logoutSession();
                      setIsOpen(false);
                      navigate('/login');
                    } catch (error) {
                      console.error('[SettlementSidebar] Logout error:', error);
                      localStorage.removeItem('account-token');
                      logoutSession();
                      setIsOpen(false);
                      navigate('/login');
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-md transition-all duration-200 hover:bg-blue-100 hover:shadow-sm group"
                >
                  <img src={LogoutIcon} alt="Logout" className="w-5 h-5" />
                  <span className="text-blue-900 font-medium group-hover:text-blue-600 transition-colors">
                    Logout
                  </span>
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <main
          id="main-content"
          className={classNames(
            'flex-1 min-w-0 min-h-0 overflow-y-auto',
            !isDesktop && 'pt-12'
          )}
        >
          <div className="px-6 pt-6 pb-[max(1.5rem,calc(env(safe-area-inset-bottom,0px)+0.75rem))]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
